import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

type Tab = 'candidates' | 'contacts' | 'sync-log';
type EnrichmentStatus = 'pending' | 'enriched' | 'failed' | 'skipped';

interface VincereRecord {
  id: number;
  vincere_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  company_name: string;
  linkedin_url: string;
  location: string;
  enrichment_status: EnrichmentStatus;
  last_synced_at: string;
  enriched_at: string | null;
  written_back_at: string | null;
}

interface SyncLogEntry {
  id: number;
  operation: string;
  entity_type: string;
  records_processed: number;
  records_succeeded: number;
  records_failed: number;
  credits_used: number;
  status: string;
  created_at: string;
  first_name: string;
  last_name: string;
  user_email: string;
}

interface Stats {
  candidates: { total: number; enriched: number; pending: number };
  contacts: { total: number; enriched: number; pending: number };
}

interface FieldMapping {
  recmapperField: string;
  vincereField: string;
}

// ─── Field Mapping Modal ──────────────────────────────────────────────────────

const RECMAPPER_FIELDS = [
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'current_employer', label: 'Current Company' },
  { key: 'job_title', label: 'Current Job Title' },
  { key: 'location', label: 'Location' },
  { key: 'summary', label: 'Summary' },
];

const VINCERE_CANDIDATE_FIELDS = [
  { key: 'mobile', label: 'Mobile' },
  { key: 'phone', label: 'Phone' },
  { key: 'work_phone', label: 'Work Phone' },
  { key: 'email', label: 'Email' },
  { key: 'work_email', label: 'Work Email' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'job_title', label: 'Job Title' },
  { key: 'company_name', label: 'Current Company' },
  { key: 'summary', label: 'Summary / Bio' },
];

const VINCERE_CONTACT_FIELDS = [
  { key: 'mobile', label: 'Mobile' },
  { key: 'phone', label: 'Phone' },
  { key: 'work_phone', label: 'Work Phone' },
  { key: 'email', label: 'Email' },
  { key: 'work_email', label: 'Work Email' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'job_title', label: 'Job Title' },
  { key: 'department', label: 'Department' },
];

const FieldMappingModal: React.FC<{
  entityType: 'candidate' | 'contact';
  onClose: () => void;
  onSave: () => void;
  token: string;
}> = ({ entityType, onClose, onSave, token }) => {
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [saving, setSaving] = useState(false);
  const vincereFields = entityType === 'candidate' ? VINCERE_CANDIDATE_FIELDS : VINCERE_CONTACT_FIELDS;

  useEffect(() => {
    fetch(`/api/vincere/mappings/${entityType}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.mappings?.length) {
          setMappings(data.mappings);
        } else {
          // Default mappings
          setMappings([
            { recmapperField: 'phone', vincereField: 'mobile' },
            { recmapperField: 'email', vincereField: 'email' },
            { recmapperField: 'linkedin_url', vincereField: 'linkedin_url' },
          ]);
        }
      })
      .catch(() => {});
  }, [entityType, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`/api/vincere/mappings/${entityType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings }),
      });
      onSave();
      onClose();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => setMappings(m => [...m, { recmapperField: '', vincereField: '' }]);
  const removeRow = (i: number) => setMappings(m => m.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: keyof FieldMapping, value: string) =>
    setMappings(m => m.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-semibold text-lg">Field Mapping</h3>
            <p className="text-sm text-gray-500 capitalize">{entityType}s — choose which MarketMapr fields write back to Vincere</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1">&times;</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs font-medium text-gray-500 uppercase mb-1">
            <span>MarketMapr field</span>
            <span>Vincere field</span>
          </div>
          {mappings.map((row, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 items-center">
              <select
                value={row.recmapperField}
                onChange={e => updateRow(i, 'recmapperField', e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="">Select field...</option>
                {RECMAPPER_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <select
                  value={row.vincereField}
                  onChange={e => updateRow(i, 'vincereField', e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                >
                  <option value="">Select field...</option>
                  {vincereFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <button onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500 text-lg leading-none">&times;</button>
              </div>
            </div>
          ))}
          <button
            onClick={addRow}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-2"
          >
            <span className="text-lg leading-none">+</span> Add mapping
          </button>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#1a2b5e] text-white rounded-md hover:bg-[#243a80] disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save mappings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Pull Modal ───────────────────────────────────────────────────────────────

const PullModal: React.FC<{
  entityType: 'candidate' | 'contact';
  onClose: () => void;
  onPull: (filters: Record<string, string>) => void;
  pulling: boolean;
}> = ({ entityType, onClose, onPull, pulling }) => {
  const [status, setStatus] = useState('');
  const [limit, setLimit] = useState('200');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-semibold text-lg">Pull {entityType}s from Vincere</h3>
            <p className="text-sm text-gray-500">Choose which records to pull into MarketMapr</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status filter</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All {entityType}s</option>
              <option value="ACTIVE">Active pipeline</option>
              <option value="PLACED">Placed</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max records to pull</label>
            <input
              type="number"
              value={limit}
              onChange={e => setLimit(e.target.value)}
              min="1"
              max="5000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Records are added to the staging area — no enrichment credits used yet.</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-xs text-amber-800">Existing records in MarketMapr will be refreshed with the latest data from Vincere.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={() => onPull({ status, limit })}
            disabled={pulling}
            className="px-4 py-2 text-sm bg-[#1a2b5e] text-white rounded-md hover:bg-[#243a80] disabled:bg-gray-400"
          >
            {pulling ? 'Pulling...' : `Pull ${entityType}s`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const VincereDashboard: React.FC = () => {
  const { token, team } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('candidates');
  const [records, setRecords] = useState<VincereRecord[]>([]);
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pulling, setPulling] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [writingBack, setWritingBack] = useState(false);
  const [showPullModal, setShowPullModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const entityType = activeTab === 'candidates' ? 'candidate' : 'contact';
  const PAGE_SIZE = 50;

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRecords = useCallback(async () => {
    if (activeTab === 'sync-log') return;
    setLoading(true);
    setSelected(new Set());
    try {
      const params = new URLSearchParams({
        entityType,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (search) params.set('search', search);
      if (statusFilter) params.set('enrichmentStatus', statusFilter);

      const res = await fetch(`/api/vincere/records?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records);
        setTotal(data.total);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeTab, entityType, page, search, statusFilter, token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/vincere/records/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch {
      // ignore
    }
  }, [token]);

  const fetchSyncLog = useCallback(async () => {
    if (activeTab !== 'sync-log') return;
    setLoading(true);
    try {
      const res = await fetch(`/api/vincere/sync-log?page=${page}&limit=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSyncLog(data.logs);
        setTotal(data.total);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setPage(0);
    setSearch('');
    setStatusFilter('');
    setSelected(new Set());
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'sync-log') fetchSyncLog();
    else fetchRecords();
  }, [fetchRecords, fetchSyncLog, activeTab]);

  const handlePull = async (filters: Record<string, string>) => {
    setPulling(true);
    try {
      const res = await fetch('/api/vincere/pull', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType, filters }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('success', data.message);
        setShowPullModal(false);
        setTimeout(() => { fetchRecords(); fetchStats(); }, 3000);
      } else {
        showToast('error', data.error || 'Pull failed');
      }
    } catch {
      showToast('error', 'Pull failed');
    } finally {
      setPulling(false);
    }
  };

  const handleEnrich = async () => {
    if (!selected.size) return;
    setEnriching(true);
    try {
      const res = await fetch('/api/vincere/enrich', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType, vincereIds: Array.from(selected) }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('success', data.message);
        setSelected(new Set());
        setTimeout(() => { fetchRecords(); fetchStats(); }, 3000);
      } else {
        showToast('error', data.error || 'Enrichment failed');
      }
    } catch {
      showToast('error', 'Enrichment failed');
    } finally {
      setEnriching(false);
    }
  };

  const handleWriteback = async () => {
    if (!selected.size) return;
    setWritingBack(true);
    try {
      const res = await fetch('/api/vincere/writeback', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType, vincereIds: Array.from(selected) }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('success', data.message);
        setSelected(new Set());
        setTimeout(() => fetchRecords(), 3000);
      } else {
        showToast('error', data.error || 'Write-back failed');
      }
    } catch {
      showToast('error', 'Write-back failed');
    } finally {
      setWritingBack(false);
    }
  };

  const toggleSelect = (vincereId: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(vincereId)) next.delete(vincereId);
      else next.add(vincereId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === records.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(records.map(r => r.vincere_id)));
    }
  };

  const selectedEnriched = records.filter(r => selected.has(r.vincere_id) && r.enrichment_status === 'enriched').length;
  const selectedUnenriched = selected.size - selectedEnriched;

  const statusBadge = (status: EnrichmentStatus) => {
    const styles: Record<EnrichmentStatus, string> = {
      pending: 'bg-gray-100 text-gray-600',
      enriched: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      skipped: 'bg-yellow-100 text-yellow-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  if (!team?.vincereConnected) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="text-center max-w-sm">
          <img src="https://recmapper.com/wp-content/uploads/2026/03/vincere-logo.jpeg" alt="Vincere" className="w-16 h-16 rounded-xl object-cover mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Connect Vincere</h2>
          <p className="text-gray-500 text-sm mb-6">
            Connect your Vincere account to enrich and refresh CRM records directly from MarketMapr.
          </p>
          <button
            onClick={() => navigate('/settings?tab=integrations')}
            className="px-5 py-2.5 bg-[#1a2b5e] text-white rounded-lg hover:bg-[#243a80] text-sm font-medium"
          >
            Connect Vincere
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img src="https://recmapper.com/wp-content/uploads/2026/03/vincere-logo.jpeg" alt="Vincere" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Vincere</h1>
            <p className="text-sm text-gray-500">Enrich and refresh your CRM records</p>
          </div>
        </div>

        {/* Stats pills */}
        {stats && (
          <div className="hidden sm:flex items-center gap-3 text-sm">
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-center">
              <p className="text-xs text-gray-500">Candidates</p>
              <p className="font-semibold text-gray-900">{stats.candidates.total.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-center">
              <p className="text-xs text-gray-500">Enriched</p>
              <p className="font-semibold text-green-700">
                {stats.candidates.enriched + stats.contacts.enriched}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-center">
              <p className="text-xs text-gray-500">Contacts</p>
              <p className="font-semibold text-gray-900">{stats.contacts.total.toLocaleString()}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-5">
        <nav className="flex gap-1">
          {(['candidates', 'contacts', 'sync-log'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-[#1a2b5e] text-[#1a2b5e]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'sync-log' ? 'Sync Log' : tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Sync Log Tab */}
      {activeTab === 'sync-log' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a2b5e]" />
            </div>
          ) : syncLog.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">No sync activity yet</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {['Operation', 'Type', 'Processed', 'Succeeded', 'Failed', 'Credits', 'Status', 'Date', 'By'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {syncLog.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">{log.operation}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{log.entity_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{log.records_processed}</td>
                    <td className="px-4 py-3 text-sm text-green-700">{log.records_succeeded}</td>
                    <td className="px-4 py-3 text-sm text-red-600">{log.records_failed || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{log.credits_used || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        log.status === 'completed' ? 'bg-green-100 text-green-700' :
                        log.status === 'running' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      }`}>{log.status}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.first_name ? `${log.first_name} ${log.last_name}` : log.user_email || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Candidates / Contacts Tab */}
      {activeTab !== 'sync-log' && (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search name, email, company..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending enrichment</option>
              <option value="enriched">Enriched</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>

            <div className="flex-1" />

            {/* Action buttons */}
            {selected.size > 0 && (
              <>
                {selectedUnenriched > 0 && (
                  <button
                    onClick={handleEnrich}
                    disabled={enriching}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {enriching ? (
                      <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    )}
                    Enrich {selectedUnenriched} record{selectedUnenriched !== 1 ? 's' : ''}
                  </button>
                )}
                {selectedEnriched > 0 && (
                  <button
                    onClick={handleWriteback}
                    disabled={writingBack}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {writingBack ? (
                      <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    Write back {selectedEnriched} to Vincere
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => setShowMappingModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Field mapping
            </button>

            <button
              onClick={() => setShowPullModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a2b5e] text-white rounded-lg text-sm hover:bg-[#243a80]"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Pull from Vincere
            </button>
          </div>

          {/* Selection summary */}
          {selected.size > 0 && (
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center justify-between">
              <span>{selected.size} record{selected.size !== 1 ? 's' : ''} selected</span>
              <button onClick={() => setSelected(new Set())} className="text-blue-600 hover:text-blue-800 text-xs">Clear</button>
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a2b5e]" />
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-16">
                <img src="https://recmapper.com/wp-content/uploads/2026/03/vincere-logo.jpeg" alt="Vincere" className="w-12 h-12 rounded-xl object-cover mx-auto mb-3 opacity-20" />
                <p className="text-gray-500 text-sm mb-4">No {activeTab} pulled yet</p>
                <button
                  onClick={() => setShowPullModal(true)}
                  className="px-4 py-2 bg-[#1a2b5e] text-white rounded-lg text-sm hover:bg-[#243a80]"
                >
                  Pull from Vincere
                </button>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.size === records.length && records.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-blue-600"
                      />
                    </th>
                    {['Name', 'Title / Company', 'Email', 'Phone', 'Location', 'Status', 'Synced', 'Written back'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {records.map(record => (
                    <tr
                      key={record.vincere_id}
                      className={`hover:bg-gray-50 ${selected.has(record.vincere_id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(record.vincere_id)}
                          onChange={() => toggleSelect(record.vincere_id)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {[record.first_name, record.last_name].filter(Boolean).join(' ') || '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{record.job_title || '—'}</p>
                        <p className="text-xs text-gray-500">{record.company_name || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{record.email || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{record.phone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{record.location || '—'}</td>
                      <td className="px-4 py-3">{statusBadge(record.enrichment_status)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(record.last_synced_at)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(record.written_back_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>{total.toLocaleString()} records total</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5">Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showPullModal && (
        <PullModal
          entityType={entityType}
          onClose={() => setShowPullModal(false)}
          onPull={handlePull}
          pulling={pulling}
        />
      )}
      {showMappingModal && (
        <FieldMappingModal
          entityType={entityType}
          onClose={() => setShowMappingModal(false)}
          onSave={() => {}}
          token={token!}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white z-50 ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default VincereDashboard;
